import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { AreaTexto, Aviso, Botao, Campo, Cartao, Carregando, Entrada } from '../componentes/ui.jsx';
import { Icone } from '../componentes/Icone.jsx';
import './RegrasCadom.css';

/**
 * Regras Cadom: o que e especifico desta clinica e muda com o tempo.
 *
 * A pagina e feita de SECOES (uma por tipo de regra) para crescer sem virar
 * bagunca. A primeira e "Contatos de referencia": pessoas que nao usam o
 * sistema, mas que a Sofia e o menu precisam indicar ao paciente — como a
 * secretaria da agenda particular do Dr. Sidney. Trocar o nome ou o numero
 * aqui vale na proxima mensagem: a Sofia le daqui, e o menu troca as
 * variaveis {nome:chave} e {telefone:chave} na hora de enviar.
 */

const CONTATO_VAZIO = { chave: '', funcao: '', nome: '', telefone: '', quando: '' };

/** "Secretária da agenda" -> "secretaria_da_agenda": a chave nasce do nome da funcao. */
function chaveDe(texto) {
  return String(texto ?? '')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
}

export function RegrasCadom() {
  const clienteQuery = useQueryClient();
  const consulta = useQuery({ queryKey: ['regras'], queryFn: () => api.get('/api/regras') });
  // Rascunho = o que a pessoa esta editando; null = acompanha o servidor.
  const [rascunho, setRascunho] = useState(null);

  const salvar = useMutation({
    mutationFn: (corpo) => api.put('/api/regras', corpo),
    onSuccess: (dados) => {
      clienteQuery.setQueryData(['regras'], dados.regras);
      setRascunho(null);
    }
  });

  if (consulta.isLoading) return <Carregando texto="Carregando as regras..." />;
  if (consulta.isError) return <Aviso tom="perigo">{consulta.error.message}</Aviso>;

  const contatos = rascunho ?? consulta.data.contatos.map(({ telefoneFormatado, ...c }) => ({ ...c, telefone: telefoneFormatado || c.telefone }));
  const mudou = rascunho !== null;
  const editar = (lista) => {
    salvar.reset();
    setRascunho(lista);
  };
  const mudarCampo = (i, campo, valor) =>
    editar(
      contatos.map((c, j) => {
        if (j !== i) return c;
        const novo = { ...c, [campo]: valor };
        // Contato novo: a chave acompanha a funcao ate alguem mexer nela.
        if (campo === 'funcao' && c.chaveAutomatica) novo.chave = chaveDe(valor);
        return novo;
      })
    );

  function gravar(e) {
    e.preventDefault();
    salvar.mutate({ contatos: contatos.map(({ chaveAutomatica, ...c }) => c) });
  }

  return (
    <form className="rg-pagina" onSubmit={gravar}>
      <header className="rg-topo">
        <div className="crescer">
          <h1>Regras Cadom</h1>
          <p className="texto-fraco">
            Regras específicas da clínica que mudam com o tempo. O que você salva aqui vale na hora para a Sofia e para o
            menu do WhatsApp.
          </p>
        </div>
        <div className="linha">
          {mudou && (
            <Botao type="button" variante="secundario" onClick={() => editar(null)} disabled={salvar.isPending}>
              Descartar
            </Botao>
          )}
          <Botao type="submit" carregando={salvar.isPending} disabled={!mudou}>
            Salvar regras
          </Botao>
        </div>
      </header>

      {salvar.isError && (
        <Aviso tom="perigo" aoFechar={salvar.reset}>
          {salvar.error.message}
        </Aviso>
      )}
      {salvar.isSuccess && !mudou && <Aviso tom="sucesso">Regras salvas. A Sofia e o menu já estão usando.</Aviso>}

      <Cartao
        titulo="Contatos de referência"
        acao={
          <Botao
            type="button"
            variante="secundario"
            tamanho="sm"
            onClick={() => editar([...contatos, { ...CONTATO_VAZIO, chaveAutomatica: true }])}
          >
            <Icone nome="adicionar" className="rg-ico" /> Novo contato
          </Botao>
        }
      >
        <p className="texto-fraco rg-explica">
          Pessoas que <strong>não usam este sistema</strong>, mas que o paciente precisa procurar. A Sofia passa o nome e
          o WhatsApp delas quando o caso combina com o campo “Quando indicar”. No menu do WhatsApp, use as variáveis de cada
          contato no texto das mensagens: trocar a pessoa aqui troca em todas as mensagens.
        </p>

        {contatos.length === 0 && <p className="texto-fraco">Nenhum contato cadastrado.</p>}

        <div className="rg-lista">
          {contatos.map((c, i) => (
            <ContatoReferencia
              key={i}
              contato={c}
              aoMudar={(campo, valor) => mudarCampo(i, campo, valor)}
              aoRemover={() => editar(contatos.filter((_, j) => j !== i))}
            />
          ))}
        </div>
      </Cartao>
    </form>
  );
}

function ContatoReferencia({ contato: c, aoMudar, aoRemover }) {
  const [copiado, setCopiado] = useState(null);
  const variaveis = c.chave ? [`{nome:${c.chave}}`, `{telefone:${c.chave}}`] : [];

  async function copiar(v) {
    try {
      await navigator.clipboard.writeText(v);
      setCopiado(v);
      setTimeout(() => setCopiado(null), 1500);
    } catch {
      setCopiado(null);
    }
  }

  return (
    <section className="rg-contato" aria-label={c.funcao || 'Contato novo'}>
      <div className="rg-contato__topo">
        <span className="rg-contato__icone" aria-hidden="true">
          <Icone nome="agente" />
        </span>
        <strong className="crescer">{c.funcao || 'Contato novo'}</strong>
        <Botao type="button" variante="perigo" tamanho="sm" onClick={aoRemover}>
          Remover
        </Botao>
      </div>

      <div className="rg-grade">
        <Campo rotulo="Função" obrigatorio dica="Ex.: Secretária da agenda particular do Dr. Sidney Chalub">
          <Entrada value={c.funcao} onChange={(e) => aoMudar('funcao', e.target.value)} maxLength={120} />
        </Campo>
        <Campo rotulo="Nome da responsável" obrigatorio>
          <Entrada value={c.nome} onChange={(e) => aoMudar('nome', e.target.value)} maxLength={60} />
        </Campo>
        <Campo rotulo="WhatsApp" dica="Com DDD. Sem número, a Sofia diz que a recepção passa o contato.">
          <Entrada value={c.telefone} onChange={(e) => aoMudar('telefone', e.target.value)} inputMode="tel" placeholder="(92) 99999-9999" />
        </Campo>
        <Campo rotulo="Chave" dica="Identifica o contato nas variáveis do menu. Só letras minúsculas, números e _.">
          <Entrada value={c.chave} onChange={(e) => aoMudar('chave', chaveDe(e.target.value))} maxLength={40} className="mono" />
        </Campo>
      </div>

      <Campo rotulo="Quando indicar" dica="O que a Sofia deve reconhecer para passar este contato ao paciente.">
        <AreaTexto rows={2} value={c.quando} onChange={(e) => aoMudar('quando', e.target.value)} maxLength={400} />
      </Campo>

      {variaveis.length > 0 && (
        <div className="rg-variaveis">
          <span className="texto-fraco">Variáveis para o menu:</span>
          {variaveis.map((v) => (
            <button key={v} type="button" className="rg-variavel mono" onClick={() => copiar(v)} title="Copiar">
              {v}
              <span className="rg-variavel__acao">{copiado === v ? 'copiado' : <Icone nome="atendimentos" className="rg-ico" />}</span>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
